// Local fixtures for the One-on-One Coach, used when VITE_USE_MOCKS routes handleRequest to the mock
// branch. Kept out of the process handlers so those stay a thin list of live calls.

import type { CoachLiveTokenConfig } from '../../../../../pure-js/types/CoachApi'
import type { CoachConversation, CoachMessage, UserGoal } from '../../../../../pure-js/types/CoachTypes'
import {
  CoachConversationFlow,
  CoachConversationMode,
  CoachMessageRole,
  CoachRole,
  GoalScope,
  GoalSource,
  GoalStatus
} from '../../../../../pure-js/types/CoachTypes'
import { createUniqueId } from '../../../../../pure-js/libs/Common'

const nowIso = () => new Date().toISOString()

const MOCK_USER_ID = 'me'

const MOCK_LIVE_TOKEN_TTL_MS = 30 * 60 * 1000 // 30 min

export const MOCK_COACH_GOALS: UserGoal[] = [
  {
    id: 'goal-speak-up',
    createdAt: nowIso(),
    userId: MOCK_USER_ID,
    goal: 'Speak up earlier in team discussions',
    priority: 1,
    status: GoalStatus.ACTIVE,
    source: GoalSource.ONBOARDING
  },
  {
    id: 'goal-clarifying-question',
    createdAt: nowIso(),
    userId: MOCK_USER_ID,
    goal: 'Ask one clarifying question before deciding',
    priority: 2,
    status: GoalStatus.ACTIVE,
    source: GoalSource.COACH_FLOW
  },
  {
    id: 'goal-ask-before-advising',
    createdAt: nowIso(),
    userId: MOCK_USER_ID,
    goal: 'Ask the team what they think before I say where I stand',
    priority: 1,
    status: GoalStatus.ACTIVE,
    source: GoalSource.COACH_FLOW,
    scope: GoalScope.LEADERSHIP
  }
]

export const MOCK_OVERALL_SUMMARY =
  "Across your chats we've focused on speaking up sooner and asking sharper questions. You've already tried it in one real meeting — nice momentum."

export const MOCK_COACH_CONVERSATIONS: CoachConversation[] = [
  {
    id: 'conversation-onboarding',
    createdAt: nowIso(),
    userId: MOCK_USER_ID,
    role: CoachRole.GAMER,
    flow: CoachConversationFlow.ONBOARDING,
    mode: CoachConversationMode.TEXT,
    startedAt: nowIso(),
    endedAt: nowIso(),
    title: 'Setting your first goals',
    summary: 'You set two goals: speaking up earlier and asking a clarifying question before deciding.',
    messageCount: 8
  },
  {
    id: 'conversation-post-game',
    createdAt: nowIso(),
    userId: MOCK_USER_ID,
    role: CoachRole.GAMER,
    flow: CoachConversationFlow.POST_GAME,
    mode: CoachConversationMode.VOICE,
    startedAt: nowIso(),
    endedAt: nowIso(),
    title: 'Reflecting on your last session',
    summary: 'We looked at how your team shared information under time pressure and one habit to try.',
    messageCount: 12
  },
  {
    id: 'conversation-leadership',
    createdAt: nowIso(),
    userId: MOCK_USER_ID,
    role: CoachRole.LEADER,
    flow: CoachConversationFlow.LEADERSHIP,
    mode: CoachConversationMode.VOICE,
    startedAt: nowIso(),
    endedAt: nowIso(),
    title: 'Where my people go quiet',
    summary: 'We looked at what the group results say about asking for help, and one thing to try on Monday.',
    messageCount: 9
  }
]

export const buildMockConversation = (conversationId: string): CoachConversation => ({
  id: conversationId,
  createdAt: nowIso(),
  userId: MOCK_USER_ID,
  role: CoachRole.GAMER,
  flow: CoachConversationFlow.FREE,
  mode: CoachConversationMode.TEXT,
  startedAt: nowIso(),
  title: 'Coaching chat'
})

export const buildMockThread = (conversationId: string): CoachMessage[] => [
  {
    id: 'message-greeting',
    createdAt: nowIso(),
    conversationId,
    role: CoachMessageRole.ASSISTANT,
    content: 'Hey — good to see you! How have things been going since we last spoke?'
  },
  {
    id: 'message-user-reply',
    createdAt: nowIso(),
    conversationId,
    role: CoachMessageRole.USER,
    content: 'Pretty good. I did speak up a bit more in our standup this week.'
  },
  {
    id: 'message-follow-up',
    createdAt: nowIso(),
    conversationId,
    role: CoachMessageRole.ASSISTANT,
    content: "That's a real win — nice. What made it feel easier this time?"
  }
]

export const buildMockGreeting = (conversationId: string): CoachMessage => ({
  id: 'message-greeting',
  createdAt: nowIso(),
  conversationId,
  role: CoachMessageRole.ASSISTANT,
  content: 'Hey — good to see you! How have things been going since we last spoke?'
})

export const buildMockReply = (conversationId: string): CoachMessage => ({
  id: `message-${createUniqueId()}`,
  createdAt: nowIso(),
  conversationId,
  role: CoachMessageRole.ASSISTANT,
  content:
    "That makes sense. What would 'speaking up earlier' actually look like in your next meeting — what's one small thing you could try?"
})

export const buildMockStartedConversation = (params: {
  flow?: CoachConversationFlow
  mode?: CoachConversationMode
}): CoachConversation => ({
  id: 'conversation-new',
  createdAt: nowIso(),
  userId: MOCK_USER_ID,
  role: CoachRole.GAMER,
  flow: params.flow || CoachConversationFlow.FREE,
  mode: params.mode || CoachConversationMode.TEXT,
  startedAt: nowIso(),
  messageCount: 1
})

export const buildMockEndedConversation = (conversationId: string): CoachConversation => ({
  ...buildMockConversation(conversationId),
  endedAt: nowIso(),
  summary: 'A quick check-in on your goals and one thing to try this week.'
})

/** Mock-mode edit: the same list back with one goal reworded and/or moved between active and achieved. */
export const buildMockEditedGoals = (patch: { goalId: string; goal?: string; status?: GoalStatus }): UserGoal[] =>
  MOCK_COACH_GOALS.map((goal) => {
    if (goal.id !== patch.goalId) return goal
    const status = patch.status ?? goal.status
    return {
      ...goal,
      goal: patch.goal ?? goal.goal,
      status,
      achievedAt: status === GoalStatus.ACHIEVED ? (goal.achievedAt ?? nowIso()) : undefined,
      updatedAt: nowIso()
    }
  })

export const buildMockSavedGoals = (
  drafts: { id?: string; goal: string; priority?: number }[],
  source: GoalSource
): UserGoal[] =>
  drafts.map((draft, index) => ({
    id: draft.id || `goal-${index}`,
    createdAt: nowIso(),
    userId: MOCK_USER_ID,
    goal: draft.goal,
    priority: draft.priority ?? index + 1,
    status: GoalStatus.ACTIVE,
    source
  }))

/**
 * A fake live-session config. Mock mode pairs it with `StubRealtimeTransport`, which opens no
 * connection, so the token goes nowhere — it only lets the session reach LIVE for offline UI work.
 */
export const buildMockLiveTokenConfig = (): CoachLiveTokenConfig => ({
  token: 'mock-live-token',
  model: 'mock-live-model',
  expiresAt: new Date(Date.now() + MOCK_LIVE_TOKEN_TTL_MS).toISOString(),
  startBefore: new Date(Date.now() + MOCK_LIVE_TOKEN_TTL_MS).toISOString()
})
